import { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Switch, Badge, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Plus } from 'lucide-react';
import { SectionCard, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import { useCan } from '@rbac/useCan';
import { useCrudList, useCrudWrite } from '../../hooks';

/**
 * What you sell (D33): the store picks product types from the platform's list
 * and keeps its own categories inside each type. A type that already has models
 * stays on (the server refuses to turn it off). A type outside the industries the
 * platform approved for the store cannot be turned on (MERCH-89; the server refuses it too).
 */
export default function ProductTypesTab() {
  const { toast } = useToast();
  const can = useCan();
  const q = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  const save = useMutation('setStoreProductTypes');
  const categories = useCrudList('Category', { limit: 500, orderBy: { column: 'sort', direction: 'asc' } });
  const write = useCrudWrite();
  const [chosen, setChosen] = useState(null);
  const [names, setNames] = useState({});
  const [err, setErr] = useState('');

  useEffect(() => {
    if (q.data) setChosen(q.data.filter((t) => t.enabled).map((t) => t.key));
  }, [q.data]);

  const canEdit = can('inventory', 'update');
  const toggle = (key, on) => setChosen((c) => (on ? [...new Set([...c, key])] : c.filter((k) => k !== key)));
  const dirty = q.data && chosen && q.data.filter((t) => t.enabled).map((t) => t.key).join() !== q.data.filter((t) => chosen.includes(t.key)).map((t) => t.key).join();

  const submit = async () => {
    setErr('');
    try {
      await save.run({ types: chosen });
      toast.success('Saved', 'Your catalogue now offers these product types.');
      q.reload();
    } catch (ex) {
      setErr(errorText(ex));
    }
  };

  const addCategory = async (typeKey) => {
    const name = String(names[typeKey] || '').trim();
    if (!name) return;
    try {
      await write.create('Category', { name, product_type: typeKey });
      setNames((n) => ({ ...n, [typeKey]: '' }));
      categories.reload();
    } catch (ex) {
      toast.error('Could not add the category', errorText(ex));
    }
  };
  const setActive = async (c, on) => {
    try {
      await write.update('Category', c.id, { is_active: on ? 1 : 0 });
      categories.reload();
    } catch (ex) {
      toast.error('Could not update the category', errorText(ex));
    }
  };

  return (
    <QueryState query={q}>
      {(types) => (
        <div className="space-y-4">
          <SectionCard
            title="What you sell"
            actions={canEdit ? <Button onClick={submit} loading={save.loading} disabled={!dirty || !chosen || !chosen.length}>Save</Button> : null}
          >
            <p className="text-sm opacity-70">Choose the kinds of products your store lists. Each type has its own specs, inspection checklist and storefront filters. You can turn on types in the industries approved for your store; ask MerchantPro to approve another industry.</p>
            {err ? <Alert variant="error">{err}</Alert> : null}
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {types.map((t) => (
                <div key={t.key} className="flex min-h-[44px] items-center justify-between gap-2 rounded-lg border border-[var(--app-border)] px-3 py-2">
                  <Checkbox
                    label={t.plural}
                    checked={!!(chosen && chosen.includes(t.key))}
                    disabled={!canEdit || t.in_use || (t.allowed === false && !t.enabled)}
                    onChange={(e) => toggle(t.key, e.target.checked)}
                  />
                  {t.in_use ? <Badge variant="secondary" size="sm">In use</Badge> : null}
                  {!t.in_use && t.allowed === false && !t.enabled ? <Badge variant="outline" size="sm">Industry not approved</Badge> : null}
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Categories">
            <p className="text-sm opacity-70">Group your models within each type, e.g. "Business laptops" or "iPhones". Shoppers browse by these.</p>
            <QueryState query={categories}>
              {(rows) => (
                <div className="space-y-4">
                  {types.filter((t) => t.enabled).map((t) => {
                    const mine = rows.filter((c) => c.product_type === t.key);
                    return (
                      <div key={t.key} className="space-y-2">
                        <h3 className="text-sm font-semibold">{t.plural}</h3>
                        {mine.length ? (
                          <ul className="divide-y divide-[var(--app-border)] rounded-lg border border-[var(--app-border)]">
                            {mine.map((c) => (
                              <li key={c.id} className="flex min-h-[44px] items-center justify-between gap-2 px-3 py-2">
                                <span className={Number(c.is_active) === 0 ? 'opacity-50' : ''}>{c.name}</span>
                                <Switch
                                  checked={Number(c.is_active) !== 0}
                                  disabled={!can('inventory', 'update')}
                                  onChange={(on) => setActive(c, on)}
                                  ariaLabel={`Show ${c.name} on the storefront`}
                                />
                              </li>
                            ))}
                          </ul>
                        ) : <p className="text-sm opacity-60">No categories yet.</p>}
                        {can('inventory', 'create') ? (
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="min-w-[200px] flex-1">
                              <Input
                                label={`New ${t.label.toLowerCase()} category`}
                                value={names[t.key] || ''}
                                maxLength={120}
                                onChange={(e) => setNames((n) => ({ ...n, [t.key]: e.target.value }))}
                              />
                            </div>
                            <Button variant="outline" leftIcon={<Plus size={16} />} onClick={() => addCategory(t.key)} disabled={!String(names[t.key] || '').trim()} loading={write.loading}>Add</Button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </QueryState>
          </SectionCard>
        </div>
      )}
    </QueryState>
  );
}
